import api from './api'

export const STAFF_DIRECTORY_PATH = '/admin/staff'

export const STAFF_DEPARTMENTS = [
  'Operations',
  'Dispatch',
  'Support',
  'Finance',
  'Verification',
  'Marketing',
  'Technology',
  'Human Resources',
  'Administration',
]

export const STAFF_EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Intern' },
]

export const STAFF_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'on_leave', label: 'On leave' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'terminated', label: 'Terminated' },
]

export const STAFF_GENDERS = ['Female', 'Male', 'Other', 'Prefer not to say']

export async function fetchStaffDirectory({
  search = '',
  department = 'all',
  status = 'all',
  employmentType = 'all',
  page = 1,
  perPage = 15,
} = {}) {
  const { data } = await api.get('/admin/staff', {
    params: {
      search: search.trim() || undefined,
      department: department || 'all',
      status: status || 'all',
      employment_type: employmentType || 'all',
      page,
      per_page: perPage,
    },
  })

  return data.data
}

export async function fetchStaffMember(staffId) {
  const { data } = await api.get(`/admin/staff/${staffId}`)
  return mapStaffMemberFromApi(data.data)
}

export async function createStaffMember(payload) {
  const { data } = await api.post('/admin/staff', payload)
  return data
}

export async function updateStaffMember(staffId, payload) {
  const { data } = await api.put(`/admin/staff/${staffId}`, payload)
  return data
}

export async function changeStaffMemberStatus(staffId, status) {
  const { data } = await api.patch(`/admin/staff/${staffId}/status`, { status })
  return data
}

export async function deleteStaffMember(staffId) {
  const { data } = await api.delete(`/admin/staff/${staffId}`)
  return data
}

const STATUS_LABELS = STAFF_STATUSES.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), {})
const EMPLOYMENT_TYPE_LABELS = STAFF_EMPLOYMENT_TYPES.reduce(
  (acc, t) => ({ ...acc, [t.value]: t.label }),
  {},
)

export function mapStaffMemberFromApi(member) {
  return {
    id: member.id,
    employeeId: member.employee_id,
    firstName: member.first_name,
    lastName: member.last_name,
    name: member.name,
    initials: member.initials,
    email: member.email,
    phone: member.phone,
    altPhone: member.alt_phone,
    gender: member.gender,
    dateOfBirth: member.date_of_birth,
    department: member.department,
    jobTitle: member.job_title,
    employmentType: member.employment_type,
    employmentTypeLabel: member.employment_type_label
      ?? EMPLOYMENT_TYPE_LABELS[member.employment_type]
      ?? member.employment_type,
    status: member.status,
    statusLabel: member.status_label ?? STATUS_LABELS[member.status] ?? member.status,
    hireDate: member.hire_date,
    endDate: member.end_date,
    address: member.address,
    city: member.city,
    state: member.state,
    country: member.country,
    managerId: member.manager_id,
    managerName: member.manager_name,
    emergencyContactName: member.emergency_contact_name,
    emergencyContactPhone: member.emergency_contact_phone,
    emergencyContactRelationship: member.emergency_contact_relationship,
    avatarUrl: member.avatar_url,
    notes: member.notes,
    createdAt: member.created_at,
    updatedAt: member.updated_at,
  }
}

export function buildStaffPaginationPages(pagination) {
  const { current_page: current, last_page: last } = pagination
  if (last <= 7) {
    return Array.from({ length: last }, (_, i) => i + 1)
  }

  const pages = [1]
  if (current > 3) pages.push('…')
  for (let p = Math.max(2, current - 1); p <= Math.min(last - 1, current + 1); p += 1) {
    pages.push(p)
  }
  if (current < last - 2) pages.push('…')
  pages.push(last)

  return pages
}

export function formatStaffPaginationSummary(pagination) {
  const from = pagination.from ?? 0
  const to = pagination.to ?? 0
  const total = pagination.total ?? 0

  return {
    showingRange: total === 0 ? '0 to 0' : `${from} to ${to}`,
    showingSuffix: `of ${total.toLocaleString()} staff`,
  }
}

export function formatStaffDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
